'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  ALL_MAPPING_FIELDS,
  REQUIRED_MAPPING_FIELDS,
  type AutoAssignmentMap,
  type MappingFieldId,
  type MappingSelections,
} from '@/components/instruments/import-mapping-utils';

interface MappingField {
  id: MappingFieldId;
  required: boolean;
  section: 'required' | 'optional';
}

const REQUIRED_FIELD_SET = new Set<MappingFieldId>(REQUIRED_MAPPING_FIELDS as readonly MappingFieldId[]);

function isRequiredField(field: MappingFieldId): boolean {
  return REQUIRED_FIELD_SET.has(field);
}

const FIELD_DEFINITIONS: MappingField[] = ALL_MAPPING_FIELDS.map((field) => ({
  id: field,
  required: isRequiredField(field),
  section: isRequiredField(field) ? 'required' : 'optional',
}));

export interface ImportMappingDialogProps {
  open: boolean;
  headers: string[];
  samples: Record<string, string[]>;
  initialSelections: MappingSelections;
  autoAssignments: AutoAssignmentMap;
  onCancel: () => void;
  onConfirm: (selections: MappingSelections, rememberSelection: boolean) => void;
  disableAnimations?: boolean;
}

export function ImportMappingDialog({
  open,
  headers,
  samples,
  initialSelections,
  autoAssignments,
  onCancel,
  onConfirm,
  disableAnimations,
}: ImportMappingDialogProps) {
  const t = useTranslations('instruments.client.import.mapping');
  const [selections, setSelections] = useState<MappingSelections>(initialSelections);
  const [rememberSelection, setRememberSelection] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const hasMountedRef = useRef(false);

  useEffect(() => {
    // On first mount we rely on the initial useState(initialSelections) value
    // to avoid an immediate state reset/re-render during the open animation.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    if (!open) {
      return;
    }

    setSelections(initialSelections);
    setRememberSelection(true);
    setError(null);
  }, [initialSelections, open]);

  const requiredFields = useMemo(
    () => FIELD_DEFINITIONS.filter((field) => field.section === 'required'),
    []
  );
  const optionalFields = useMemo(
    () => FIELD_DEFINITIONS.filter((field) => field.section === 'optional'),
    []
  );
  const selectedHeaders = useMemo(() => {
    const taken = new Set<string>();
    Object.values(selections).forEach((value) => {
      if (typeof value === 'string' && value.trim().length > 0) {
        taken.add(value);
      }
    });
    return taken;
  }, [selections]);
  const hasMissingRequiredFields = useMemo(
    () => requiredFields.some((field) => !selections[field.id]),
    [requiredFields, selections],
  );

  const handleSelectChange = (fieldId: MappingFieldId, headerValue: string) => {
    setSelections((prev) => {
      if (headerValue === '__none') {
        return {
          ...prev,
          [fieldId]: null,
        };
      }

      const updated = { ...prev };
      const previousFieldEntry = Object.entries(prev).find(
        ([id, value]) => id !== fieldId && value === headerValue,
      );
      if (previousFieldEntry) {
        const [previousFieldId] = previousFieldEntry as [MappingFieldId, string];
        updated[previousFieldId] = null;
      }

      updated[fieldId] = headerValue;
      return updated;
    });
  };

  const handleConfirm = () => {
    const missingRequired = requiredFields.filter((field) => !selections[field.id]);
    if (missingRequired.length > 0) {
      setError(t('errors.missingRequired'));
      return;
    }
    setError(null);
    onConfirm(selections, rememberSelection);
  };

  const renderField = (field: MappingField) => {
    const isOptional = !field.required;
    const selectValue = selections[field.id] ?? (isOptional ? '__none' : undefined);
    const fieldLabel = t(`fields.${field.id}.label`);
    const description = t(`fields.${field.id}.description`);
    const helperKey = `fields.${field.id}.helper`;
    const helperText = t(helperKey, { defaultValue: '' });
    const hasHelperText = helperText.trim().length > 0;
    const autoAssignment = autoAssignments[field.id];
    const isAutoMatched =
      autoAssignment &&
      selections[field.id] &&
      autoAssignment.header === selections[field.id];
    const badgeLabel = autoAssignment
      ? autoAssignment.reason === 'stored'
        ? t('badges.stored')
        : t('badges.auto')
      : null;

    const selectedHeader = selections[field.id];
    const sampleValues = selectedHeader ? samples[selectedHeader] ?? [] : [];

    return (
      <div
        key={field.id}
        data-testid={`mapping-field-${field.id}`}
        className="rounded-lg border border-muted bg-background p-4 shadow-sm transition-colors"
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
          <div className="space-y-3 xl:w-72">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-foreground">{fieldLabel}</span>
              <Badge
                variant="secondary"
                className={cn(
                  'uppercase tracking-wide',
                  field.required ? 'bg-amber-100 text-amber-900' : 'bg-muted text-muted-foreground'
                )}
              >
                {field.required ? t('badges.required') : t('badges.optional')}
              </Badge>
              {isAutoMatched && badgeLabel ? (
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-900">
                  {badgeLabel}
                </Badge>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
            {hasHelperText ? (
              <p className="text-xs text-muted-foreground italic leading-relaxed">{helperText}</p>
            ) : null}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col gap-2">
              <Select
                value={selectValue}
                onValueChange={(value) => handleSelectChange(field.id, value)}
              >
                <SelectTrigger
                  className={cn(
                    'w-full shadow-sm',
                    field.required && !selections[field.id] && 'border-destructive'
                  )}
                  aria-label={fieldLabel}
                >
                  <SelectValue placeholder={t('placeholders.chooseColumn')} />
                </SelectTrigger>
                {/* SelectContent uses elevated z-index in the shared UI library to remain above Dialog overlays */}
                <SelectContent className="max-h-[240px]">
                  {field.required ? null : (
                    <SelectItem value="__none">{t('placeholders.skipColumn')}</SelectItem>
                  )}
                  {headers.map((header) => (
                    <SelectItem key={header} value={header}>
                      {header}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedHeader
                  ? t('placeholders.mappedTo', { column: selectedHeader })
                  : field.required
                    ? t('placeholders.requiredHint')
                    : t('placeholders.optionalHint')}
              </p>
            </div>
          </div>

          <div className="min-w-[220px] space-y-2 rounded-md border border-dashed border-muted px-3 py-2 bg-muted/40">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t('samples.label')}
            </p>
            {sampleValues.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {sampleValues.map((value, index) => (
                  <span
                    key={`${field.id}-${value}-${index}`}
                    className="rounded-full bg-background px-2 py-0.5 text-xs text-foreground shadow-sm"
                  >
                    {value}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">
                {t('samples.empty')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onCancel()}>
      <DialogContent
        disableAnimations={disableAnimations}
        className="w-full max-w-[min(100vw-2rem,1120px)] sm:max-w-[min(100vw-2rem,1120px)] lg:max-w-[min(100vw-4rem,1280px)] overflow-hidden p-0"
      >
        <div className="flex h-full max-h-[85vh] flex-col">
          <DialogHeader className="px-6 pt-6 pb-4">
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>

          <div className="px-6 pb-4 text-sm text-muted-foreground">
            <p>{t('summary.instructions')}</p>
            <p className="mt-1">{t('summary.columnCount', { count: headers.length })}</p>
          </div>

          <div className="flex-1 overflow-y-auto border-y border-muted/60 bg-muted/20 px-6 py-5">
            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {t('sections.required')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('sections.requiredHelp')}
                </p>
              </div>
              <div className="space-y-3">
                {requiredFields.map(renderField)}
              </div>
            </section>

            <section className="mt-8 space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">
                  {t('sections.optional')}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t('sections.optionalHelp')}
                </p>
              </div>
              <div className="space-y-3">
                {optionalFields.map(renderField)}
              </div>
            </section>
          </div>

          <div className="px-6 py-5">
            <div className="flex items-start gap-2 rounded-md border border-muted bg-background p-3">
              <Checkbox
                id="remember-mapping"
                checked={rememberSelection}
                onCheckedChange={(checked) => setRememberSelection(Boolean(checked))}
              />
              <div>
                <label
                  htmlFor="remember-mapping"
                  className="text-sm font-medium text-foreground"
                >
                  {t('remember.label')}
                </label>
                <p className="text-xs text-muted-foreground">
                  {t('remember.description')}
                </p>
              </div>
            </div>

            {error && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <p>{error}</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex items-center justify-end gap-2 border-t border-muted/60 px-6 py-4">
            <Button variant="outline" onClick={onCancel}>
              {t('actions.cancel')}
            </Button>
            <Button onClick={handleConfirm} disabled={hasMissingRequiredFields}>
              {t('actions.confirm')}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
